import React, { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { Dialog, DialogTitle, IconButton, Typography, DialogActions, Button, TextField, Autocomplete, FormControl, Switch, Box, DialogContent, Avatar, Stack } from '@mui/material'; 
import CloseIcon from '@mui/icons-material/Close';  
import { GenerateReportRequest, Store } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient'; 
import useLogError from '../../../hooks/useLogError';  
import { useTranslation } from 'react-i18next';  
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { File06 } from '../../../assets';
import { theme } from '../../../theme';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';

export interface Generate {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  reportId: number;
}

const GenerateReport: React.FC<Generate> = ({ open, onClose,  onConfirm, reportId }) => {
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [reportName, setReportName] = useState<string>('');
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false); 
  const { user } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);  
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false); 
  const [snackBarSeverity, setSnackBarSeverity] = useState<'error' | 'warning' | 'success'>('error');
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const { generateReport, getUpdateReport } = useNswagClient();
  const { logError } = useLogError();
  const { t } = useTranslation('reportBuilder');

  useEffect(() => {
    if (reportId) {
      getUpdateReport(reportId)
        .then((report) => {
          setReportName(report.data?.reportName!); 
        })
        .catch((error) => {
          logError(error);
        });
    }
  }, [reportId]);
   
  const getMaxDate = () => {
    const today = new Date().toISOString().split('T')[0];
    return today;
  };

  const getMinDate = () => {
    const minDate = new Date('1900-01-01').toISOString().split('T')[0];
    return minDate;
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores([]);
    setCheckedAllStores(checked);
  };

  const setSnackBarProps = (severity: 'error'  | 'warning' | 'success' , message: string) => { 
    setIsSnackBarOpen(true);
    setSnackBarSeverity(severity);
    setSnackbarMessage(message);
  };
    
  const handleGenerateReport = () => { 
    
    const newReportRequest: GenerateReportRequest = {
      reportId: reportId,
      from:  fromDate,
      to: toDate,
      stores: checkedAllStores ? (user?.stores?.map(x=>x.storeNumber as string)?? []) : selectedStores.map(x => x.storeNumber as string),
      reportOnAllAvailableStores: true, 
    };
    generateReport(newReportRequest);
    setSnackBarProps('success', 'Report generation has been queued.'); 
    onConfirm(); 
  }; 

  const closeSnackBar = () => {
    setIsSnackBarOpen(false);
  }; 

  const isGenerateDisabled = !fromDate || !toDate || (!checkedAllStores && selectedStores.length === 0);

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='sm'
        open={open}
        onClose={close}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          '& .MuiDialog-container': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      > 
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                backgroundColor: () => theme.palette.secondary[100],
                color: () => theme.palette.secondary[700],
              }}
            >
         
              <File06 color={theme.palette.secondary[500]} />
            </Avatar>
          </Box> 
          <IconButton
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.custom.gray[800],
              mr: '-10px',
            }}
          >
            <CloseIcon />
          </IconButton> 
          
        </DialogTitle>
        <DialogContent>
          <Stack>
            <Typography variant='textLG'
              fontWeight={600}>  {`${t('generateReport')} - ${reportName}`}</Typography> 
            <Typography color="textSecondary"
              sx={{ mt: 1 , whiteSpace: 'pre-line' }}
              variant="textSM">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' ,  flexDirection: { xs: 'column', sm: 'row' } }}> 
                <Box sx={{ display: 'flex', gap: '12px',  pt: '10px' ,   width: '100%',     flexDirection: { xs: 'column', sm: 'row' } }}> 
                  <Box sx={{ flex: 1, color: (theme) => theme.palette.custom.gray[600] }}>
                    <DatePickerLocalized
                      useCalendar={false}
                      label={t('fromDate')}
                      value={fromDate}
                      translations={{
                        clearButtonLabel: t('calendar.clearButtonLabel'),
                        todayButtonLabel: t('calendar.todayButtonLabel'),
                        cancelButtonLabel: t('calendar.cancelButtonLabel'),
                        okButtonLabel: t('calendar.okButtonLabel'),
                      }}
                      onChange={(newValue) => {
                        if(newValue == ''){
                          setFromDate('');
                        }
                        if (newValue) { 
                          setFromDate(newValue);    
                        }  
                      }}  
                      shouldDisableDate={(date) => {
                        const minDate = getMinDate();
                        const maxDate = toDate !== '' ? toDate : getMaxDate();
                        return date < minDate || date > maxDate;
                      }} 
                    />
                  </Box> 
                  <Box sx={{ flex: 1, color: (theme) => theme.palette.custom.gray[600] }}>
                    <DatePickerLocalized
                      useCalendar={false}
                      label={t('toDate')}
                      value={toDate}
                      translations={{
                        clearButtonLabel: t('calendar.clearButtonLabel'),
                        todayButtonLabel: t('calendar.todayButtonLabel'),
                        cancelButtonLabel: t('calendar.cancelButtonLabel'),
                        okButtonLabel: t('calendar.okButtonLabel'),
                      }}
                      onChange={(newValue) => {
                        if(newValue == ''){
                          setToDate('');
                        }
                        else if (newValue) { 
                          setToDate(newValue);   
                        } 
                      }} 
                      shouldDisableDate={(date) => {
                        const maxDate = getMaxDate();
                        const minDate = fromDate !== '' ? fromDate : getMinDate();
                        return date < minDate || date > maxDate;
                      }} 
                    />
                  </Box>
                </Box>
 
                <Box sx={{ display: 'flex', gap: '12px', width: '100%', flexDirection: { xs: 'column', sm: 'row' } }}>  
                  <Box sx={{ flex: 1, color: (theme) => theme.palette.custom.gray[600],  maxWidth: { xs: '100%', sm: 270 } }}> 
                    <FormControl fullWidth>
                      <Autocomplete
                        fullWidth
                        multiple
                        limitTags={2}
                        options={user?.stores ?? []}
                        value={selectedStores}
                        disabled={checkedAllStores}
                        onChange={handleSelectChange}
                        getOptionKey={(option) => option.storeNumber ?? ''}
                        getOptionLabel={(option) => option.storeName ?? ''}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required={selectedStores.length === 0 && !checkedAllStores}
                            label={t('selectStores')}
                            placeholder={t('selectStores')}
                          />
                        )}
                      />
                    </FormControl>
                  </Box>
 
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, width: 'auto', gap: '8px' }}>
                    <Typography sx={{ whiteSpace: 'nowrap' }}>{t('allStore')}</Typography>
                    <Switch
                      checked={checkedAllStores}
                      disabled={selectedStores.length > 0}
                      onChange={handleSelectAllChange}
                      sx={{
                        flexShrink: 0,  
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Typography>
          </Stack>
        </DialogContent> 
        <DialogActions sx={{ 
          padding: '0px 24px 24px', 
          '&.MuiDialogActions-root': {
            padding: '0px 24px 24px', 
          },
        }}>
          <Stack direction={{ xs: 'column', md: 'row' }}
            gap='12px'
            width='100%'>
            <Button
              fullWidth
              sx={{ fontSize: '16px' }} 
              variant="secondary"
              size="md"
              onClick={onClose}>
              {t('close')}
            </Button>
            <Button
              disabled={isGenerateDisabled}
              fullWidth
              variant="primary"
              size="md"
              sx={{ fontSize: '16px' }} 
              onClick={() => handleGenerateReport()}
            >
              {t('generate')}
            </Button>
      
          </Stack>
        </DialogActions> 
      </Dialog>   
    
      <MessagesSnackbar onClose={closeSnackBar}
        open={isSnackBarOpen}
        severity={snackBarSeverity}
        message={snackbarMessage}
        duration={null} /></>
  
  );
};

export default GenerateReport;
